/**
 * CR-048: 라우트 패턴 → 페이지 제목표. 탭 제목과 브라우저 창 제목에 쓴다 (화면 안 PageHeader 제목은 별개).
 * 패턴은 routes.tsx 의 APP_CHILDREN path 와 같다. 표에 없는 라우트는 개발 중 콘솔 경고 후 메뉴 라벨 → 경로 순으로 폴백.
 * 원본: planning/changes/CR-048-tab-identity-titles.html 「페이지 제목표」
 */
import { matchRoutes, type RouteObject } from 'react-router';

export const ROUTE_TITLES: ReadonlyArray<readonly [pattern: string, title: string]> = [
  ['/', '대시보드'],
  ['dashboard/ciso', 'CISO 전사 현황'],
  ['notices', '공지사항'],
  ['notices/:id', '공지 상세'],
  ['schedule/todos', '할 일'],
  ['schedule/calendar', '캘린더'],
  ['schedule/obligations', '의무 일정'],
  ['schedule/obligations/new', '의무 일정 등록'],
  ['schedule/obligations/:id/edit', '의무 일정 편집'],
  ['schedule/obligations/:id', '의무 일정 상세'],
  ['settings/notify', '알림 설정'],
  ['policies', '규정 문서 목록'],
  ['policies/new', '규정 문서 등록'],
  ['policies/:id', '규정 상세보기'],
  ['policies/versions/:id', '규정 버전 보기'],
  ['policies/versions/:id/edit', '규정 초안 편집'],
  ['policies/changes/:id', '개정 요청'],
  ['policies/reviews', '검토 대장'],
  ['policies/reviews/:id', '검토 태스크'],
  ['policies/regulations', '기준 개정 이벤트'],
  ['policies/regulations/new', '기준 개정 등록'],
  ['policies/regulations/:id', '기준 개정 상세'],
  ['evidence', '증적 항목'],
  ['evidence/items/new', '증적 항목 등록'],
  ['evidence/items/:id', '증적 항목 상세'],
  ['evidence/versions/:id', '증적 버전 보기'],
  ['evidence/requests', '증적 요청함'],
  ['assessments/runs', '점검 회차'],
  ['assessments/runs/new', '점검 회차 생성'],
  ['assessments/runs/:id', '점검 회차 상세'],
  ['assessments/tasks', '개선과제'],
  ['assessments/tasks/new', '개선과제 등록'],
  ['assessments/tasks/:id', '개선과제 상세'],
  ['assessments/templates', '점검표 템플릿'],
  ['assessments/templates/new', '점검표 템플릿 등록'],
  ['assessments/templates/:id', '점검표 템플릿 상세'],
  ['vendors', '수탁사'],
  ['vendors/new', '수탁사 등록'],
  ['vendors/:id', '수탁사 상세'],
  ['vendors/outsourcings', '위탁 업무'],
  ['vendors/outsourcings/new', '위탁 업무 등록'],
  ['vendors/outsourcings/:id', '위탁 업무 상세'],
  ['vendors/provisions', '제3자 제공'],
  ['vendors/provisions/new', '제3자 제공 등록'],
  ['vendors/provisions/:id/edit', '제3자 제공 편집'],
  ['vendors/transfers', '국외이전'],
  ['vendors/transfers/new', '국외이전 등록'],
  ['vendors/transfers/:id/edit', '국외이전 편집'],
  ['ledgers/pii-files', '개인정보 파일'],
  ['ledgers/pii-files/new', '개인정보 파일 등록'],
  ['ledgers/pii-files/:id/edit', '개인정보 파일 편집'],
  ['ledgers/pii-files/:id', '개인정보 파일 상세'],
  ['ledgers/areas', '보호구역'],
  ['ledgers/areas/new', '보호구역 등록'],
  ['ledgers/areas/:id/edit', '보호구역 편집'],
  ['ledgers/areas/:id', '보호구역 상세'],
  ['ledgers/pii-handlers', '개인정보취급자'],
  ['ledgers/pii-handlers/new', '취급자 등록'],
  ['ledgers/pii-handlers/:id/edit', '취급자 편집'],
  ['ledgers/pii-handlers/:id', '취급자 상세'],
  ['ledgers/wifi', '무선네트워크'],
  ['ledgers/wifi/new', '무선네트워크 등록'],
  ['ledgers/wifi/:id/edit', '무선네트워크 편집'],
  ['ledgers/wifi/:id', '무선네트워크 상세'],
  ['ledgers/wifi/devices', '무선 장비'],
  ['ledgers/wifi/devices/new', '무선 장비 등록'],
  ['ledgers/wifi/devices/:id/edit', '무선 장비 편집'],
  ['ledgers/wifi/devices/:id', '무선 장비 상세'],
  // CR-056 범용 대장: 목록 제목은 메뉴 라벨(대장 이름)로 폴백('' = 표에 없는 것으로 처리)
  ['ledgers/g/:code', ''],
  ['ledgers/g/:code/new', '대장 기록 등록'],
  ['ledgers/g/:code/:id/edit', '대장 기록 편집'],
  ['ledgers/g/:code/:id', '대장 기록'],
  ['approvals', '결재함'],
  ['approvals/new', '기안하기'],
  ['approvals/:id', '결재 상세'],
  ['controls', '통제항목 마스터'],
  ['controls/:id', '통제항목 상세'],
  ['audit', '접속기록'],
  ['account', '내 계정'],
  ['users', '사용자'],
  ['users/invite', '사용자 초대'],
  ['users/:id', '사용자 상세'],
  ['users/:id/roles', '사용자 역할 배정'],
  ['settings/org', '조직·부서'],
  ['settings/org/new', '부서 등록'],
  ['settings/org/:id/edit', '부서 편집'],
  ['settings/roles', '역할·권한'],
  ['settings/roles/new', '역할 만들기'],
  ['settings/roles/:id', '역할 편집'],
  ['settings/attributes', '속성 정의'],
  ['settings/attributes/new', '속성 등록'],
  ['settings/attributes/:id/edit', '속성 편집'],
  ['settings/auth', '인증 설정'],
  ['settings/auth/sso/new', 'IdP 등록'],
  ['settings/auth/sso/edit', 'IdP 편집'],
  ['settings/auth/sso/test/:stateId', 'SSO 테스트 결과'],
  ['settings/approvals', '결재선 설정'],
  ['settings/branding', '브랜딩'],
  ['settings/domains', '도메인'],
  ['settings/domains/new', '도메인 등록'],
  ['settings/domains/:id', '도메인 상세'],
  ['platform', '플랫폼 개요'],
  ['platform/tenants', '테넌트'],
  ['platform/tenants/new', '테넌트 생성'],
  ['platform/tenants/:id', '테넌트 상세'],
  ['platform/tenants/:id/users', '테넌트 사용자'],
  ['platform/tenants/:id/sso', '테넌트 SSO 설정'],
  ['platform/tenants/:id/sso/edit', 'IdP 편집'],
  ['platform/tenants/:id/sso/test/:stateId', 'SSO 테스트 결과'],
  ['platform/tenants/:id/users/invite', '테넌트 사용자 초대'],
  ['platform/tenants/:tid/users/:id/roles', '테넌트 사용자 역할'],
  ['platform/users', '전체 사용자'],
  ['platform/accounts', '계정'],
  ['platform/accounts/:id', '계정 상세'],
  ['platform/support', '지원 열람 세션'],
  ['platform/domains', '전체 도메인'],
  ['platform/operators', '운영자 계정'],
  ['platform/operators/invite', '운영자 초대'],
  ['platform/notices', '플랫폼 공지'],
  ['platform/notices/new', '공지 등록'],
  ['platform/notices/:id', '공지 상세'],
  ['platform/audit', '활동 로그'],
];
const BY_PATTERN = new Map<string, string>(ROUTE_TITLES);

let routesRef: RouteObject[] | null = null;
/** 라우트 정의는 순환 import 를 피하려고 앱 부팅 때 한 번 등록한다 (routes.tsx) */
export function registerRoutes(children: RouteObject[]) {
  routesRef = [{ path: '/', children }];
  if (import.meta.env.DEV) {
    for (const r of children) { const p = r.index ? '/' : (r.path ?? ''); if (!BY_PATTERN.has(p)) console.warn(`[routeTitles] 제목표에 없는 라우트: ${p}`); }
  }
}

/** 경로(쿼리 제외) → 라우트 패턴. 매치되지 않으면 경로 그대로 */
export function routePattern(path: string): string {
  const pathname = path.split('?')[0].split('#')[0] || '/';
  if (!routesRef) return pathname;
  const m = matchRoutes(routesRef, pathname);
  const last = m?.[m.length - 1];
  if (!last) return pathname;
  if (last.route.index) return '/';
  return last.route.path ?? pathname;
}

export function routeTitle(path: string): string | null {
  return BY_PATTERN.get(routePattern(path)) ?? null;
}
